import { CognitoUser } from '@aws-amplify/auth';
import { useNavigate } from '@tanstack/react-location';
import React, { useEffect, useState } from 'react';
import useAuthentication from '../../../api/use-authentication';
import useUserManagement from '../../../api/use-user-management';
import { useFlexAssessmentContext } from '../../../context/context';
import buttons from '../../../css/buttons.module.scss';
import styles from '../../../css/modal.module.scss';
import { FlexAssessmentContextAction } from '../../../types/flex-assessment-context-action';
import { NotificationType } from '../../../types/notification-type';
import { RegistrationFieldName } from '../../../types/registration-field-name';
import { ProgramType } from '../../../types/types';
import { PAGE_URLS } from '../../../utils/constants';
import { MIXPANEL_EVENTS } from '../../../utils/mixpanel-consts';
import { track } from '../../../utils/mixpanel-helper';
import { STRINGS } from '../../../utils/strings';
import { validateEmail, validatePassword } from '../../../utils/validation';
import Input from '../../form-fields/input';
import Spinner, { SPINNER_COLORS } from '../../spinner';
import { HASH_URLS } from '../auth-modal';

export const LoginForm = React.memo((): JSX.Element => {
  const navigate = useNavigate();
  const { signIn } = useAuthentication();
  const { state, dispatch } = useFlexAssessmentContext();
  const { enrollUserInProgram, initializeUser } = useUserManagement();
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState<boolean | undefined>(false);
  const [isPasswordValid, setIsPasswordValid] = useState<boolean | undefined>(
    false
  );

  const isFormValidForSubmission = isEmailValid && isPasswordValid;

  useEffect(() => {
    dispatch({
      type: FlexAssessmentContextAction.setFieldValue,
      field: RegistrationFieldName.email,
      value: email,
      isValid: isEmailValid,
    });
  }, [email, dispatch, isEmailValid]);

  const onForgotPassword = (
    event: React.FormEvent<HTMLButtonElement>
  ): void => {
    event.preventDefault();
    track(MIXPANEL_EVENTS.BUTTON_CLICK, { button: 'forgot-password' });
    navigate({ to: `#${HASH_URLS.RESET_PASSWORD}` });
  };

  const login = async () => {
    setIsSubmittingForm(true);
    track(MIXPANEL_EVENTS.BUTTON_CLICK, { button: 'login' });

    let userId = null;
    let user: CognitoUser | Error;
    let eligCaseId = null;

    try {
      user = await signIn(email, password);
      if (user instanceof CognitoUser) {
        userId = user.getSignInUserSession()?.getIdToken().payload.sub!;
      }
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: `${STRINGS.ERRORS.SOMETHING_WRONG_CONTACT_SUPPORT}. Error: ${STRINGS.ERRORS.SIGN_IN_ERROR}`,
          type: NotificationType.Error,
        },
      });
      return;
    }

    // 4. Put the user in the Program
    try {
      const eligibilityData = eligCaseId ? { caseId: eligCaseId } : {};
      await enrollUserInProgram(userId, user, {
        program: ProgramType.flex,
        accessCode: state.accessCode,
        eligibilityData,
        programEnrollmentData: {
          assessmentId: state.assessmentId,
        },
      });
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: `${STRINGS.ERRORS.SOMETHING_WRONG_CONTACT_SUPPORT}. Error: ${STRINGS.ERROR.ENROLLMENT_ERROR}`,
          type: NotificationType.Error,
        },
      });
      return;
    }

    // 5. Onboard/initialize the program
    try {
      await initializeUser(userId, { program: ProgramType.flex });
      navigate({ to: PAGE_URLS.SUCCESS });
    } catch (error) {
      dispatch({
        type: FlexAssessmentContextAction.showNotification,
        payload: {
          message: `${STRINGS.ERRORS.SOMETHING_WRONG_CONTACT_SUPPORT}. Error: ${STRINGS.ERROR.INIT_ERROR}`,
          type: NotificationType.Error,
        },
      });
      return;
    }

    setIsSubmittingForm(false);
  };

  return (
    <form className={styles.modalContent}>
      <h3 className={styles.modalTitle}>Login</h3>
      <p className={styles.p}>
        Your email is already in the system. Please log—in.
      </p>
      <Input
        type="email"
        value={email}
        name="email"
        placeholder="Email Address"
        validator={(value: string): boolean => validateEmail(value)}
        errorString={STRINGS.ERRORS.EMAIL_NOT_VALID}
        disabled={isSubmittingForm}
        callback={(
          name: string,
          value: string,
          isValid: boolean | undefined
        ): void => {
          setEmail(value);
          setIsEmailValid(isValid);
        }}
      />
      <div className={styles.passwordAndForgotButtonContainer}>
        <Input
          type="password"
          autoComplete="current-password"
          value={password}
          name="password"
          placeholder="Password"
          validator={(value: string): boolean => validatePassword(value)}
          errorString={STRINGS.ERRORS.INVALID_INPUT}
          disabled={isSubmittingForm}
          callback={(
            name: string,
            value: string,
            isValid: boolean | undefined
          ): void => {
            setPassword(value);
            setIsPasswordValid(isValid);
          }}
        />
        <button
          onClick={onForgotPassword}
          className={styles.forgotPasswordButton}
        >
          Forgot password
        </button>
      </div>
      <button
        onClick={login}
        className={buttons.submitBtn}
        disabled={!isFormValidForSubmission || isSubmittingForm}
      >
        {isSubmittingForm && <Spinner color={SPINNER_COLORS.LIGHT} />}
        Continue
      </button>
    </form>
  );
});
